import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";

export function LoginPage({ onAuthed }: { onAuthed: () => void }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password) {
      setError("Mot de passe requis");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await api.login(password);
      onAuthed();
      toast.success("Connecté");
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-dvh grid lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[var(--color-navy-700)] text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          <Logo variant="white" height={36} />
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-[var(--color-coral-300)] text-sm font-medium uppercase tracking-wider mb-3">
            CRM Closer
          </p>
          <h2 className="text-3xl font-bold leading-tight">
            Tes prospects et tes commissions sous la main.
          </h2>
          <p className="mt-4 text-[var(--color-navy-100)] leading-relaxed">
            Pipeline, relances, échéancier de paiement, calcul des commissions
            après les 14 jours de rétractation.
          </p>
        </div>
        <div className="relative z-10 text-xs text-[var(--color-navy-200)]">
          © {new Date().getFullYear()} · CRM personnel
        </div>
        {/* Decorative bolt */}
        <div
          aria-hidden
          className="absolute -right-20 -bottom-20 opacity-10 pointer-events-none"
        >
          <svg width="500" height="500" viewBox="0 0 56 56">
            <path
              d="M32 6 L8 36 L22 36 L18 50 L44 22 L30 22 L32 6 Z"
              fill="#E94B3F"
            />
          </svg>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 lg:p-12 bg-[var(--color-background)]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo height={32} />
          </div>
          <h1 className="text-2xl font-bold text-[var(--color-navy-700)]">
            Connexion
          </h1>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            CRM mono-utilisateur. Pas d'inscription, juste le mot de passe
            défini au déploiement.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Mot de passe
              </span>
              <input
                type="password"
                className="input-base mt-1.5"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                required
              />
            </label>
            {error && (
              <p
                role="alert"
                className="text-sm text-[var(--color-coral-600)]"
              >
                {error}
              </p>
            )}
            <button
              type="submit"
              className="btn btn-accent w-full"
              disabled={submitting}
            >
              {submitting ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
